<template>
    <Modal :show="isLogoutWarningVisible"
           :backdrop="false"
           :keyboard="false"
           :header="true"
           :showCloseButton="countdownNumber > 0"
           :title="countdownNumber > 0 ? 'Timeout Warning' : 'Session expired'"
           @close="handleContinueSession"
    >
        <div v-if="countdownNumber > 0">
            <p>
                Due to inactivity for <strong>{{ Math.floor(idleTime / 60) }}</strong> minutes, your session with EMS|MC
                will expire in
            </p>
            <h2>{{ countdownNumber }} seconds.</h2>
            <div class="text-right">
                <button @click="handleContinueSession" type="button" class="btn btn-primary btn-sm">
                    Continue Session
                </button>
            </div>
        </div>
        <div v-else>
            <Loader wait-text="Logging out due to inactivity..."/>
        </div>
    </Modal>
</template>

<script>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";

import {getCurrentUnixTime} from "@/lib/time";
import {createLocalStorage} from "@/lib/storage";

import Modal from "./Modal.vue";
import Loader from "@/components/Loader.vue";

const events = [
    'keypress',
    'change',
    'click',
    'mousemove',
    'scroll',
    'resize'
];

const state = createLocalStorage('session-timeout-modal');

const getLastActiveTime = () => state.get('last-active-time', null) || getCurrentUnixTime();
const touchActiveTime = () => {
    state.set('last-active-time', getCurrentUnixTime());
    window.dispatchEvent(new Event('storage'));
};
const reloadState = () => state.reload();

export default {
    name: "SessionTimeoutModal",
    components: {
        Loader,
        Modal
    },
    props: {
        idleTime: {
            type: Number,
            default: 1800
        },
        countdownTime: {
            type: Number,
            default: 60
        }
    },
    setup(props, {emit}) {

        let mainInterval = null;

        const activeTime = ref(getLastActiveTime());
        const nowTime = ref(getCurrentUnixTime());

        const isLogoutWarningVisible = ref(false);

        const countdownNumber = computed(() => {
            if (!isLogoutWarningVisible.value) {
                return props.countdownTime;
            }

            return Math.max(0, props.countdownTime - getIdleSeconds() + props.idleTime);
        });

        onMounted(() => startTicking());
        onBeforeUnmount(() => stopTicking());

        const handleContinueSession = () => {
            isLogoutWarningVisible.value = false;
            updateTimeToNow();
        };

        const getIdleSeconds = () => Math.max(0, nowTime.value - activeTime.value);

        const checkIdleTimeout = () => {
            const diff = getIdleSeconds();

            isLogoutWarningVisible.value = diff >= props.idleTime;

            nowTime.value = getCurrentUnixTime();

            if (countdownNumber.value === 0) {
                stopTicking();
                emit('logout');
            }
        };

        const updateTimeToNow = () => {
            if (isLogoutWarningVisible.value) {
                return;
            }

            touchActiveTime();
            activeTime.value = getLastActiveTime();
        };

        const handleStorageUpdated = () => {
            reloadState();
            activeTime.value = getLastActiveTime();
            checkIdleTimeout();
        };

        const startTicking = () => {
            updateTimeToNow();
            checkIdleTimeout();

            events.forEach((event) => document.addEventListener(event, updateTimeToNow));
            mainInterval = setInterval(() => checkIdleTimeout(), 1000);
            window.addEventListener('storage', handleStorageUpdated);
        };

        const stopTicking = () => {
            events.forEach((event) => document.removeEventListener(event, updateTimeToNow));
            window.removeEventListener('storage', handleStorageUpdated);
            clearInterval(mainInterval);
        };

        return {
            isLogoutWarningVisible,
            countdownNumber,
            handleContinueSession
        };
    }
}
</script>